// iter154 — Single source of truth for day-kind semantics.
// Three day kinds now (backend `dailyEntries.dayKind` string):
//   - "working"        : normal working day with MDM or DR served.
//   - "working_no_mdm" : NEW — school open but no meal served (flood,
//                        cyclone, cook absent, grain stock-out, etc.).
//                        attendance is all zero, ingredients empty.
//   - "holiday"        : school closed (Sunday, government holiday …).
//
// Rules the whole app relies on:
//   - isWorkingDay(entry) → true for "working" AND "working_no_mdm"
//     (both count toward the school's monthly day-count on the admin
//     dashboard & MDCF). Holidays are NOT working days.
//   - isMealDay(entry)    → true ONLY for "working" (meal actually
//     served; drives rice, cooking cost, Quarterly UC, Daily
//     Consumption).
//   - isNoMdmDay(entry)   → true ONLY for "working_no_mdm" (meal NOT
//     served; shown as a grey chip on dashboards, excluded from meal
//     counts, included in working-day totals).
//
// Legacy records with no dayKind field are treated as "working" via the
// backend default.
export const DAY_KINDS = ["working", "working_no_mdm", "holiday"];

export function dayKindOf(entry) {
  const k = entry?.dayKind;
  return k === "working_no_mdm" || k === "holiday" ? k : "working";
}

export function isWorkingDay(entry) {
  const k = dayKindOf(entry);
  return k === "working" || k === "working_no_mdm";
}

export function isMealDay(entry) {
  return dayKindOf(entry) === "working";
}

// Read-time normalisation: No-MDM / Holiday days never carry meal data,
// so legacy rows saved with entryType "dryration" or leftover numbers are
// treated as entryType "mdm" with zero attendance and no ingredients.
export function normalizeEntry(entry) {
  if (!entry || isMealDay(entry)) return entry;
  return {
    ...entry,
    entryType: "mdm",
    attendance: { balVatika: 0, primary: 0, upperPrimary: 0, highSchool: 0 },
    ingredients: [],
  };
}

export function isNoMdmDay(entry) {
  return dayKindOf(entry) === "working_no_mdm";
}

export function isHolidayDay(entry) {
  return dayKindOf(entry) === "holiday";
}

// Reason catalogue shown as chips when the user picks working_no_mdm.
// Keys are persisted on the entry; the labels + Odia strings are only
// for display and never for persistence.
export const NO_MDM_REASONS = [
  { key: "heavy_rain",        en: "Heavy Rain",                                od: "ପ୍ରବଳ ବର୍ଷା" },
  { key: "cyclone",           en: "Cyclone",                                   od: "ବାତ୍ୟା" },
  { key: "flood",             en: "Flood",                                     od: "ବନ୍ୟା" },
  { key: "heatwave",          en: "Heatwave",                                  od: "ଗ୍ରୀଷ୍ମ ପ୍ରବାହ" },
  { key: "collector_order",   en: "School closed by District Collector order", od: "ଜିଲ୍ଲାପାଳଙ୍କ ଆଦେଶରେ ବନ୍ଦ" },
  { key: "stock_out",         en: "Food grain / cooking cost not available",   od: "ଖାଦ୍ୟଶସ୍ୟ / ରନ୍ଧନ ଖର୍ଚ୍ଚ ଉପଲବ୍ଧ ନାହିଁ" },
  { key: "cook_absent",       en: "Cook absent",                               od: "ରୋଷେୟା ଅନୁପସ୍ଥିତ" },
  { key: "other",             en: "Other",                                     od: "ଅନ୍ୟାନ୍ୟ" },
];
export const NO_MDM_REASON_KEYS = NO_MDM_REASONS.map((r) => r.key);

export function reasonLabel(key, lang = "en") {
  const r = NO_MDM_REASONS.find((x) => x.key === key);
  if (!r) return key || "";
  return lang === "od" ? r.od : r.en;
}
